import React, { useState, useEffect, useRef } from "react";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";
import { connect } from "react-redux";
import selectDownArrow from "../../../assets/images/selectDownArrow.svg";
import { actionFetchCountries } from "../../../common/redux/actions/actionCountry";

const SharedCountryDropdown = ({
  label = "Country",
  placeholder = "Select Country",
  selectedCountry,
  setSelectedCountry,
  disabled = false,
  onCountryChange = () => {},
  // Redux
  countries,
  fetchCountries,
}) => {
  const [countryOptions, setCountryOptions] = useState([]);
  const [countrySearch, setCountrySearch] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Trigger fetch on mount (no-ops if already in store)
  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  // Build sorted options whenever the store list changes
  useEffect(() => {
    if (countries && countries.length > 0) {
      const sorted = [...countries]
        .sort((a, b) => a.countryName.localeCompare(b.countryName))
        .map((item) => ({
          label: item.countryName,
          value: item.countryName,
          code: item.countryCode || "",
          currency: {
            code: item.currency || "",
            symbol: item.currencySymbol || "",
          },
          distanceUnit: item.distanceUnit || "Km",
          weightUnit: item.weightUnit || "KG",
        }));
      setCountryOptions(sorted);
    }
  }, [countries]);

  const handleCountrySelect = (selectedName) => {
    const selected = countryOptions.find((c) => c.value === selectedName);
    setSelectedCountry(selectedName);
    onCountryChange({
      name: selectedName,
      currency: selected?.currency?.code || "",
      currencySymbol: selected?.currency?.symbol || "",
      countryCode: selected?.code || "",
      distanceUnit: selected?.distanceUnit || "Km",
      weightUnit: selected?.weightUnit || "KG",
    });
    setCountrySearch("");
    setDropdownOpen(false);
  };

  const toggleDropdown = () => setDropdownOpen((prev) => !prev);
  const openDropdown = () => setDropdownOpen(true);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setCountrySearch(selectedCountry || "");
  }, [selectedCountry]);

  return (
    <Form.Group className="custom-form">
      {label && <Form.Label className="color-head">{label}</Form.Label>}

      <div
        className={`country-dropdown-wrapper ${disabled ? "disabled" : ""}`}
        ref={dropdownRef}
        aria-disabled={disabled}
      >
        <InputGroup
          className="country-search-box"
          onClick={() => {
            if (!disabled) toggleDropdown();
          }}
        >
          <Form.Control
            type="text"
            className="country-input"
            placeholder={placeholder}
            value={countrySearch}
            disabled={disabled}
            onChange={(e) => {
              const value = e.target.value;
              setCountrySearch(value);
              if (!disabled) setDropdownOpen(true);
              if (value.trim() === "") {
                setSelectedCountry("");
                onCountryChange({
                  name: "",
                  currency: "",
                  currencySymbol: "",
                  countryCode: "",
                  distanceUnit: "",
                  weightUnit: "",
                });
              }
            }}
            aria-disabled={disabled}
          />
          <InputGroup.Text
            className="dropdown-arrow-wrapper"
            onClick={(e) => {
              e.stopPropagation();
              if (!disabled) openDropdown();
            }}
            style={{
              backgroundColor: "transparent",
              border: "none",
              cursor: disabled ? "not-allowed" : "pointer",
            }}
            aria-hidden={disabled}
          >
            <img
              src={selectDownArrow}
              alt="CaretDown"
              className="dropdown-arrow"
            />
          </InputGroup.Text>
        </InputGroup>

        {dropdownOpen && (
          <div className="country-dropdown-list">
            {countryOptions
              .filter((item) =>
                item.label
                  .toLowerCase()
                  .includes(countrySearch.toLowerCase().trim())
              )
              .map((item) => (
                <div
                  key={item.value}
                  onClick={() => {
                    if (!disabled) handleCountrySelect(item.value);
                  }}
                  className={`country-option ${disabled ? "disabled" : ""}`}
                  role="button"
                  tabIndex={disabled ? -1 : 0}
                >
                  {item.label}
                </div>
              ))}
          </div>
        )}
      </div>
    </Form.Group>
  );
};

const mapStateToProps = (state) => ({
  countries: state.country.countries,
});

const mapDispatchToProps = (dispatch) => ({
  fetchCountries: () => dispatch(actionFetchCountries()),
});

export default connect(mapStateToProps, mapDispatchToProps)(SharedCountryDropdown);
